import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Phone, MessageSquare, MessageSquareOff, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export function ConsentBadge({ contact, onChanged, editable }) {
  if (!contact?.phone) return null;
  const ok = !!contact.sms_consent;
  const Icon = ok ? ShieldCheck : ShieldOff;
  const cls = ok ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" : "text-muted-foreground border-border bg-muted/40";
  const toggle = async (e) => {
    e.stopPropagation();
    if (!editable) return;
    if (!window.confirm(ok ? "Revoke texting consent for this contact?" : "Record that this contact gave you permission to text them (e.g. verbally or by email)?")) return;
    try { const { data } = await api.patch(`/contacts/${contact.id}`, { sms_consent: !ok }); toast.success(data.sms_consent ? "Texting consent recorded" : "Texting consent revoked"); onChanged?.(data); } catch (ex) { toast.error(err(ex)); }
  };
  return (
    <span onClick={toggle} title={ok ? `Consent given${contact.sms_consent_source ? ` via ${contact.sms_consent_source}` : ""}${contact.sms_consent_at ? ` · ${new Date(contact.sms_consent_at).toLocaleString()}` : ""}` : "No texting consent on file"}
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-px rounded border whitespace-nowrap ${cls} ${editable ? "cursor-pointer hover:opacity-80" : ""}`} data-testid={`consent-badge-${contact.id}`}>
      <Icon className="w-2.5 h-2.5" />{ok ? "Texting OK" : "No texting"}
    </span>
  );
}

export function ContactCallText({ contact, compact, onChanged }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  if (!contact?.phone) return null;
  const ok = !!contact.sms_consent;
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/messages/text", { contact_id: contact.id, body: body.trim(), channel: "sms" }); toast.success(`Text sent to ${contact.phone}`); setBody(""); setOpen(false); onChanged?.(); }
    catch (ex) { toast.error(err(ex)); } finally { setBusy(false); }
  };
  const stop = (e) => e.stopPropagation();
  return (
    <span className="inline-flex items-center gap-1" onClick={stop}>
      <a href={`tel:${contact.phone}`} onClick={stop} title={`Call ${contact.phone}`} data-testid={`call-${contact.id}`}>
        <Button type="button" variant={compact ? "ghost" : "outline"} size={compact ? "icon" : "sm"} className={compact ? "h-6 w-6" : "h-8 text-xs"}><Phone className="w-3.5 h-3.5" />{!compact && <span className="ml-1.5">Call</span>}</Button>
      </a>
      <Button type="button" variant={compact ? "ghost" : "outline"} size={compact ? "icon" : "sm"} className={`${compact ? "h-6 w-6" : "h-8 text-xs"} ${ok ? "" : "opacity-40"}`} disabled={!ok} onClick={() => setOpen(true)} title={ok ? `Text ${contact.phone}` : "No texting consent — ask the buyer to opt in first"} data-testid={`text-${contact.id}`}>
        {ok ? <MessageSquare className="w-3.5 h-3.5" /> : <MessageSquareOff className="w-3.5 h-3.5" />}{!compact && <span className="ml-1.5">Text</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="text-dialog" onClick={stop}>
          <DialogHeader><DialogTitle>Text {contact.first_name || contact.phone}</DialogTitle><DialogDescription>Sent from your Twilio number to {contact.phone}. Replies land in the Inbox.</DialogDescription></DialogHeader>
          <form onSubmit={send} className="space-y-3">
            <Textarea data-testid="text-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={`Hi ${contact.first_name || "there"}, this is …`} required />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span>{body.length}/320</span><Button type="submit" size="sm" disabled={busy || !body.trim()} data-testid="text-send">{busy ? "Sending…" : "Send text"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
